import { apiClient } from '@/api/client';
import { useAuthContext } from '@/components/auth';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

/**
 * ============================================
 * Profile Hooks
 * ============================================
 *
 * Hooks for user profile management:
 * - useProfile: Get current user profile with stats
 * - useUpdateProfile: Update user info
 */

// ============================================
// Types
// ============================================

/**
 * User Statistics
 */
export interface UserStats {
  /** Total number of tasks */
  totalTasks: number;
  /** Number of completed tasks */
  completedTasks: number;
  /** Number of learning days */
  learningDays: number;
}

/**
 * Current Child Summary (embedded in profile)
 */
export interface CurrentChildSummary {
  id: string;
  nickname: string;
  avatar: string;
  grade: string;
}

/**
 * User Profile Response
 */
export interface UserProfile {
  id: string;
  email: string | null;
  name: string | null;
  currentChild: CurrentChildSummary | null;
  stats: UserStats;
  createdAt: string;
  memberSince: string;
}

/**
 * Update Profile Request
 */
export interface UpdateProfileRequest {
  name?: string;
}

/**
 * API Success Response wrapper
 */
interface SuccessResponse<T> {
  success: boolean;
  data: T;
}

// ============================================
// API Functions
// ============================================

/**
 * Fetch current user profile
 */
async function fetchProfile(): Promise<SuccessResponse<UserProfile>> {
  const response = await apiClient.get<SuccessResponse<UserProfile>>('/users/me');
  return response.data;
}

/**
 * Update current user profile
 */
async function updateProfile(data: UpdateProfileRequest): Promise<SuccessResponse<UserProfile>> {
  const response = await apiClient.patch<SuccessResponse<UserProfile>>('/users/me', data);
  return response.data;
}

// ============================================
// Query Keys
// ============================================

export const profileKeys = {
  all: ['profile'] as const,
  me: () => [...profileKeys.all, 'me'] as const,
};

// ============================================
// Hooks
// ============================================

/**
 * Hook for fetching current user profile
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useProfile();
 *
 * if (data?.data) {
 *   console.log(data.data.name, data.data.stats);
 * }
 * ```
 */
export function useProfile(options?: { enabled?: boolean }) {
  const { canMakeAuthenticatedRequests } = useAuthContext();

  const enabled = canMakeAuthenticatedRequests && (options?.enabled ?? true);

  return useQuery({
    queryKey: profileKeys.me(),
    queryFn: fetchProfile,
    enabled,
  });
}

/**
 * Hook for updating user profile
 *
 * @example
 * ```tsx
 * const { mutate, isPending } = useUpdateProfile();
 *
 * mutate({ name: "New Name" });
 * ```
 */
export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateProfile,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: profileKeys.me() });
    },
  });
}
